// oxlint-disable complexity
/**
 * Order Confirmation Component
 * Displays success/failure results after order placement with on-demand status checking
 */
import {oneWeekDates} from '@hconnect/uikit'
import {CheckCircle, Error, Schedule, Warning} from '@mui/icons-material'
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CircularProgress,
  useTheme
} from '@mui/material'
import moment from 'moment'
import queryString from 'query-string'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import {RmcOrder, RmcOrderPlacementResult, RmcOrderStatusNavigationState} from '../../types/order'
import {TransactionResultType} from '../../types/confirmationResult'

import {OrderStatusCard} from './OrderStatusCard'

/**
 * Props for the Order Confirmation component
 */
interface RmcOrderConfirmationProps {
  placementResult: RmcOrderPlacementResult
  placedOrders: RmcOrder[]
  failedOrders: RmcOrder[]
}

/**
 * Order Confirmation component showing placement results with on-demand status checking
 */
export const RmcOrderConfirmation: React.FC<RmcOrderConfirmationProps> = ({
  placementResult,
  placedOrders,
  failedOrders
}) => {
  const {t} = useTranslation()
  const history = useHistory<RmcOrderStatusNavigationState>()
  const theme = useTheme()
  const statusColors = {
    blue: theme.palette.info.main,
    green: theme.palette.success.main,
    orange: theme.palette.warning.main,
    red: theme.palette.error.main
  }

  const hasSuccessfulOrders = placedOrders.length > 0
  const hasFailedOrders = failedOrders.length > 0
  const isPartialSuccess = hasSuccessfulOrders && hasFailedOrders
  const isCompleteFailure = !hasSuccessfulOrders && hasFailedOrders
  const isCompleteSuccess = hasSuccessfulOrders && !hasFailedOrders
  const [automaticChecksInProgress, setAutomaticChecksInProgress] = useState<Set<string>>(
    () =>
      new Set(
        placedOrders
          .filter((order) => Boolean(placementResult.requestIds?.[order.id]))
          .map((order) => order.id)
      )
  )
  const [orderStatuses, setOrderStatuses] = useState<Map<string, TransactionResultType>>(new Map())
  const isProcessing = automaticChecksInProgress.size > 0
  const areAllOrdersConfirmed =
    hasSuccessfulOrders && placedOrders.every((order) => orderStatuses.get(order.id) === 'success')
  const areAllOrdersPendingDispatch =
    hasSuccessfulOrders &&
    placedOrders.every((order) => orderStatuses.get(order.id) === 'pendingDispatch')

  const handleAutomaticStatusCheckChange = useCallback((orderId: string, isActive: boolean) => {
    setAutomaticChecksInProgress((currentOrderIds) => {
      const nextOrderIds = new Set(currentOrderIds)

      if (isActive) {
        nextOrderIds.add(orderId)
      } else {
        nextOrderIds.delete(orderId)
      }

      return nextOrderIds
    })
  }, [])

  const handleStatusChange = useCallback(
    (orderId: string, status: TransactionResultType | null) => {
      setOrderStatuses((currentStatuses) => {
        const nextStatuses = new Map(currentStatuses)

        if (status) {
          nextStatuses.set(orderId, status)
        } else {
          nextStatuses.delete(orderId)
        }

        return nextStatuses
      })
    },
    []
  )

  const handleGoToOrders = () => {
    const oneWeek = oneWeekDates()
    const rmcOrderRequests = placedOrders.flatMap((order) => {
      const requestId = placementResult.requestIds[order.id]
      const deliveryDate = order.formData.dateTime?.[0]

      if (!requestId || !deliveryDate) return []

      return [
        {
          requestId,
          material: order.formData.material?.materialDescription ?? '',
          quantity: order.formData.quantity,
          deliveryDate: new Date(deliveryDate).toISOString(),
          poNumber: order.formData.poNumber || undefined
        }
      ]
    })

    // Find the max delivery date from all placed orders
    const maxDeliveryDate = placedOrders.reduce((maxDate, order) => {
      const deliveryDate = order.formData.dateTime?.[0]
        ? moment(order.formData.dateTime[0])
        : maxDate
      return deliveryDate > maxDate ? deliveryDate : maxDate
    }, oneWeek[1])

    history.push({
      pathname: '/orders',
      search: queryString.stringify({
        dateFrom: oneWeek[0].toISOString(),
        dateTo: maxDeliveryDate.endOf('day').toISOString()
      }),
      state: {rmcOrderRequests}
    })
  }

  return (
    <Box
      sx={{
        padding: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        maxWidth: '600px',
        margin: '0 auto'
      }}
    >
      {/* Header with overall status */}
      <Box sx={{textAlign: 'center', marginBottom: '24px'}}>
        {isProcessing && (
          <>
            <CircularProgress
              size={48}
              sx={{color: statusColors.blue, marginBottom: '12px'}}
              data-test-id="confirmation-processing-spinner"
            />
            <Typography
              variant="h5"
              sx={{fontWeight: 600, color: statusColors.blue, marginBottom: '8px'}}
            >
              {t('orderIntake.rmc.confirmation.processingTitle')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('orderIntake.rmc.confirmation.processingDescription')}
            </Typography>
          </>
        )}

        {!isProcessing && isCompleteSuccess && areAllOrdersConfirmed && (
          <>
            <CheckCircle sx={{fontSize: '48px', color: statusColors.green, marginBottom: '12px'}} />
            <Typography
              variant="h5"
              sx={{fontWeight: 600, color: statusColors.green, marginBottom: '8px'}}
            >
              {t('orderIntake.rmc.confirmation.confirmedTitle')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('orderIntake.rmc.confirmation.allOrdersConfirmedSuccessfully', {
                count: placedOrders.length
              })}
            </Typography>
          </>
        )}

        {!isProcessing && isCompleteSuccess && areAllOrdersPendingDispatch && (
          <>
            <Schedule sx={{fontSize: '48px', color: statusColors.blue, marginBottom: '12px'}} />
            <Typography
              variant="h5"
              sx={{fontWeight: 600, color: statusColors.blue, marginBottom: '8px'}}
            >
              {t('orderIntake.rmc.confirmation.statusHeaders.pendingDispatch')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('orderIntake.rmc.confirmation.statusDescriptions.pendingDispatch')}
            </Typography>
          </>
        )}

        {!isProcessing &&
          isCompleteSuccess &&
          !areAllOrdersConfirmed &&
          !areAllOrdersPendingDispatch && (
            <>
              <Warning sx={{fontSize: '48px', color: statusColors.orange, marginBottom: '12px'}} />
              <Typography
                variant="h5"
                sx={{fontWeight: 600, color: statusColors.orange, marginBottom: '8px'}}
              >
                {t('orderIntake.rmc.confirmation.requestsSubmittedTitle')}
              </Typography>
              <Typography variant="body1" color="textSecondary">
                {t('orderIntake.rmc.confirmation.allOrdersPlacedSuccessfully', {
                  count: placedOrders.length
                })}
              </Typography>
            </>
          )}

        {!isProcessing && isPartialSuccess && (
          <>
            <Warning sx={{fontSize: '48px', color: statusColors.orange, marginBottom: '12px'}} />
            <Typography
              variant="h5"
              sx={{fontWeight: 600, color: statusColors.orange, marginBottom: '8px'}}
            >
              {t('orderIntake.rmc.confirmation.partialSuccessTitle')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('orderIntake.rmc.confirmation.ordersPlacedFailed', {
                count: placedOrders.length,
                failedCount: failedOrders.length
              })}
            </Typography>
          </>
        )}

        {!isProcessing && isCompleteFailure && (
          <>
            <Error sx={{fontSize: '48px', color: statusColors.red, marginBottom: '12px'}} />
            <Typography
              variant="h5"
              sx={{fontWeight: 600, color: statusColors.red, marginBottom: '8px'}}
            >
              {t('orderIntake.rmc.confirmation.completeFailureTitle')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('orderIntake.rmc.confirmation.ordersFailed', {count: failedOrders.length})}
            </Typography>
          </>
        )}
      </Box>

      {/* Successful Orders Section */}
      {hasSuccessfulOrders && (
        <Card
          sx={{
            marginBottom: '16px',
            border: `1px solid ${
              isProcessing || areAllOrdersPendingDispatch
                ? statusColors.blue
                : areAllOrdersConfirmed
                  ? statusColors.green
                  : statusColors.orange
            }`
          }}
        >
          <CardContent>
            <Alert
              severity={
                isProcessing || areAllOrdersPendingDispatch
                  ? 'info'
                  : areAllOrdersConfirmed
                    ? 'success'
                    : 'warning'
              }
              sx={{marginBottom: '16px'}}
            >
              <AlertTitle>
                {t(
                  isProcessing
                    ? 'orderIntake.rmc.confirmation.processingOrdersTitle'
                    : areAllOrdersPendingDispatch
                      ? 'orderIntake.rmc.confirmation.processingOrdersTitle'
                      : areAllOrdersConfirmed
                        ? 'orderIntake.rmc.confirmation.confirmedOrdersTitle'
                        : 'orderIntake.rmc.confirmation.successfulOrdersTitle'
                )}
              </AlertTitle>
              {t('orderIntake.rmc.confirmation.ordersPlaced', {count: placedOrders.length})}
            </Alert>

            <Box>
              {placedOrders.map((order) => {
                const requestId = placementResult.requestIds?.[order.id]
                return (
                  <OrderStatusCard
                    key={order.id}
                    order={order}
                    requestId={requestId}
                    initialStatus="submitted"
                    onAutomaticStatusCheckChange={(isActive) =>
                      handleAutomaticStatusCheckChange(order.id, isActive)
                    }
                    onStatusChange={(status) => handleStatusChange(order.id, status)}
                  />
                )
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Failed Orders Section */}
      {hasFailedOrders && (
        <Card sx={{marginBottom: '16px', border: `1px solid ${statusColors.red}`}}>
          <CardContent>
            <Alert severity="error" sx={{marginBottom: '16px'}}>
              <AlertTitle>{t('orderIntake.rmc.confirmation.failedOrdersTitle')}</AlertTitle>
              {t('orderIntake.rmc.confirmation.ordersFailed', {count: failedOrders.length})}
            </Alert>

            <Box>
              {failedOrders.map((order) => {
                const errorMessage =
                  placementResult.errors[order.id] ||
                  placementResult.errors.general ||
                  t('orderIntake.rmc.confirmation.unknownError')

                return (
                  <OrderStatusCard
                    key={order.id}
                    order={order}
                    requestId={undefined}
                    initialStatus="failed"
                    errorMessage={errorMessage}
                  />
                )
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* General Error Information */}
      {placementResult.errors.general && (
        <Alert severity="error" sx={{marginBottom: '16px'}}>
          <AlertTitle>{t('orderIntake.rmc.confirmation.systemErrorTitle')}</AlertTitle>
          {placementResult.errors.general}
        </Alert>
      )}

      {/* Action Buttons */}
      <Box sx={{display: 'flex', justifyContent: 'center', gap: '12px'}}>
        <Button
          variant="outlined"
          onClick={handleGoToOrders}
          sx={{flex: 1, maxWidth: '300px'}}
          data-test-id="rmc-confirmation-go-to-orders-button"
        >
          {t('orderIntake.rmc.confirmation.goToOrders')}
        </Button>
      </Box>
    </Box>
  )
}
